/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unloanorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { getLoanNumber, setLoanNumber as setLoan } from '../storage/userStorage';
import useLoanData from '../common/hooks/useLoanData';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

type loanValue = LoanData & {
  loadingLoanData?: boolean;
  refetchingLoanData?: boolean;
  setLoanNumber?: Dispatch<SetStateAction<string>>;
  refetchLoanData?: Dispatch<void>;
};

const LoanContext = createContext<loanValue | null>(null);

const useLoanContext = () => {
  const loanContext = useContext(LoanContext);
  if (loanContext === undefined) {
    // throwing this error helps in development when the context is out of scope.
    throw new Error('loanContext must be inside a LoanProvider');
  }
  return loanContext;
};

type loanProviderProps = {
  children: ReactNode;
};

const LoanProvider = ({ children }: loanProviderProps): JSX.Element => {
  const location = useLocation();
  const loanNo = useMemo(() => location?.state?.loanNumber || getLoanNumber() || '', [location]);
  const [loanNumber, updateLoanNumber] = useState(getLoanNumber() || '');
  const { loanData, loadingLoanData, refetchingLoanData, refetch } = useLoanData(loanNumber);

  const setLoanNumber = useCallback((value) => {
    updateLoanNumber(value);
    setLoan(value);
  }, []);
  useEffect(() => {
    loanNo && setLoanNumber(loanNo);
  }, [loanNo, setLoanNumber]);

  const refetchLoanData = () => {
    refetch({ throwOnError: true, cancelRefetch: true });
  };

  return (
    <LoanContext.Provider
      value={{ loanNumber, ...loanData, loadingLoanData, refetchingLoanData, setLoanNumber, refetchLoanData }}
    >
      {children}
    </LoanContext.Provider>
  );
};

export { LoanProvider, useLoanContext };
